import { useEffect } from 'react'

/**
 * Drives one decode attempt per delivered camera frame.
 *
 * Uses `requestVideoFrameCallback` where available (Safari 15.4+, Chrome), which
 * fires exactly when the camera produces a new frame. That is strictly better
 * than the old app's fixed `fps: 10` timer in both directions: it never decodes
 * the same frame twice when the camera is slower than the timer, and it never
 * skips frames when the camera is faster.
 *
 * `requestAnimationFrame` is the fallback, throttled so an idle high-refresh
 * display doesn't spin the decoder at 120Hz for no benefit.
 */
export function useFramePump(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  pump: (video: HTMLVideoElement) => void,
  active: boolean,
): void {
  useEffect(() => {
    const video = videoRef.current
    if (video === null || !active) return

    let cancelled = false
    let rvfcHandle: number | null = null
    let rafHandle: number | null = null

    type WithRvfc = HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number
      cancelVideoFrameCallback?: (handle: number) => void
    }
    const el = video as WithRvfc
    const hasRvfc = typeof el.requestVideoFrameCallback === 'function'

    if (hasRvfc) {
      const onFrame = (): void => {
        if (cancelled) return
        pump(video)
        rvfcHandle = el.requestVideoFrameCallback!(onFrame)
      }
      rvfcHandle = el.requestVideoFrameCallback!(onFrame)
    } else {
      const MIN_INTERVAL_MS = 1000 / 30
      let last = 0
      const onTick = (now: number): void => {
        if (cancelled) return
        if (now - last >= MIN_INTERVAL_MS) {
          last = now
          pump(video)
        }
        rafHandle = requestAnimationFrame(onTick)
      }
      rafHandle = requestAnimationFrame(onTick)
    }

    // Both callbacks are already suspended while the page is hidden, but iOS can
    // deliver a stale frame on the way back; stopping explicitly avoids
    // decoding it and reporting a scan the user never took.
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') cancelled = true
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      if (rvfcHandle !== null && typeof el.cancelVideoFrameCallback === 'function') {
        el.cancelVideoFrameCallback(rvfcHandle)
      }
      if (rafHandle !== null) cancelAnimationFrame(rafHandle)
    }
  }, [videoRef, pump, active])
}
